/**
 * Quick Add Button State Fix
 * 
 * This script ensures that quick add buttons don't get stuck in "Loading..." state,
 * and properly reset after cart operations.
 */

(function() {
  // Configuration
  const DEBUG = true; // Set to false in production
  
  // Debug logging function
  function log(message, data) {
    if (DEBUG) {
      console.log(`[ButtonFix] ${message}`, data || '');
    }
  }
  
  // Button states and text
  const BUTTON_STATES = {
    NORMAL: 'normal',
    LOADING: 'loading',
    ADDED: 'added',
    ERROR: 'error'
  };
  
  // Default text for each state (can be overridden per button)
  const DEFAULT_TEXT = {
    [BUTTON_STATES.NORMAL]: 'ADD TO CART',
    [BUTTON_STATES.LOADING]: 'LOADING...',
    [BUTTON_STATES.ADDED]: 'ADDED!',
    [BUTTON_STATES.ERROR]: 'ERROR'
  };
  
  // Track button states
  const buttonStates = new Map();
  
  document.addEventListener('DOMContentLoaded', function() {
    log('Initializing button state fix');
    
    // Initial setup
    fixButtonStates();
    
    // Set up observer to watch for new buttons
    setupButtonObserver();
    
    // Monitor cart drawer open/close events
    monitorCartDrawer();
    
    // Check button states periodically
    setInterval(resetStuckButtons, 2000);
  });
  
  // Find and fix all quick add buttons
  function fixButtonStates() {
    // Target all quick add buttons (both original and our custom ones)
    const quickAddButtons = document.querySelectorAll(
      '.quick-add-btn, ' +
      'button.add-to-cart, ' +
      'button.add_to_cart, ' +
      'button[name="add"]:not([form]), ' +
      '.add_to_cart_button'
    );
    
    log(`Found ${quickAddButtons.length} quick add buttons to fix`);
    
    quickAddButtons.forEach(button => {
      // Skip if already fixed
      if (button.hasAttribute('data-button-state-fixed')) {
        return;
      }
      
      // Mark as fixed
      button.setAttribute('data-button-state-fixed', 'true');
      
      // Store original text if not already set
      if (!button.hasAttribute('data-original-text')) {
        button.setAttribute('data-original-text', button.innerHTML || DEFAULT_TEXT[BUTTON_STATES.NORMAL]);
      }
      
      // Ensure LOADING state doesn't get stuck
      button.addEventListener('click', function(event) {
        // Only handle non-form buttons (ones without a parent form)
        if (!button.closest('form')) {
          trackButtonState(button, BUTTON_STATES.LOADING);
          
          // Set a timeout to reset button if it gets stuck
          setTimeout(() => {
            if (getButtonState(button) === BUTTON_STATES.LOADING) {
              log('Button stuck in loading state, resetting', button);
              resetButtonState(button);
            }
          }, 5000); // Wait 5 seconds before assuming it's stuck
        }
      });
      
      // Reset button if it currently appears to be in loading state
      if (isButtonInLoadingState(button)) {
        log('Found button in loading state during initialization, resetting', button);
        resetButtonState(button);
      }
    });
  }
  
  // Check if a button appears to be in loading state
  function isButtonInLoadingState(button) {
    const buttonText = button.textContent.trim().toLowerCase();
    return buttonText === 'loading' || 
           buttonText === 'loading...' || 
           button.classList.contains('loading') ||
           button.disabled;
  }
  
  // Track button state
  function trackButtonState(button, state) {
    if (!button) return;
    
    buttonStates.set(button, {
      state: state,
      timestamp: Date.now()
    });
    
    // Update button appearance
    updateButtonAppearance(button, state);
  }
  
  // Get current button state
  function getButtonState(button) {
    if (!buttonStates.has(button)) {
      return BUTTON_STATES.NORMAL;
    }
    return buttonStates.get(button).state;
  }
  
  // Reset button to normal state
  function resetButtonState(button) {
    if (!button) return;
    
    trackButtonState(button, BUTTON_STATES.NORMAL);
  }
  
  // Update button appearance based on state
  function updateButtonAppearance(button, state) {
    // Skip if button was removed from DOM
    if (!button || !button.parentNode) return;
    
    const originalText = button.getAttribute('data-original-text') || DEFAULT_TEXT[BUTTON_STATES.NORMAL];
    
    switch (state) {
      case BUTTON_STATES.LOADING:
        button.innerHTML = DEFAULT_TEXT[BUTTON_STATES.LOADING];
        button.disabled = true;
        button.classList.add('loading');
        break;
        
      case BUTTON_STATES.ADDED:
        button.innerHTML = DEFAULT_TEXT[BUTTON_STATES.ADDED];
        button.disabled = false;
        button.classList.remove('loading');
        button.classList.add('added');
        
        // Reset to normal after delay
        setTimeout(() => {
          if (getButtonState(button) === BUTTON_STATES.ADDED) {
            resetButtonState(button);
          }
        }, 2000);
        break;
        
      case BUTTON_STATES.ERROR:
        button.innerHTML = DEFAULT_TEXT[BUTTON_STATES.ERROR];
        button.disabled = false;
        button.classList.remove('loading');
        button.classList.add('error');
        
        // Reset to normal after delay
        setTimeout(() => {
          if (getButtonState(button) === BUTTON_STATES.ERROR) {
            resetButtonState(button);
          }
        }, 2000);
        break;
        
      case BUTTON_STATES.NORMAL:
      default:
        button.innerHTML = originalText;
        button.disabled = false;
        button.classList.remove('loading', 'added', 'error');
        break;
    }
  }
  
  // Check for and reset any buttons stuck in loading state
  function resetStuckButtons() {
    buttonStates.forEach((stateData, button) => {
      // If button has been in loading state for more than 5 seconds, reset it
      if (stateData.state === BUTTON_STATES.LOADING && 
          (Date.now() - stateData.timestamp > 5000)) {
        log('Found stuck button during periodic check, resetting', button);
        resetButtonState(button);
      }
    });
    
    // Also look for any buttons that appear to be in loading state but aren't tracked
    const loadingButtons = document.querySelectorAll('button.loading, button:disabled, button:contains("Loading")');
    loadingButtons.forEach(button => {
      if (isQuickAddButton(button) && !buttonStates.has(button)) {
        log('Found untracked loading button, resetting', button);
        resetButtonState(button);
      }
    });
  }
  
  // Check if a button is a quick add button
  function isQuickAddButton(button) {
    return button.classList.contains('quick-add-btn') ||
           button.classList.contains('add-to-cart') ||
           button.classList.contains('add_to_cart') ||
           button.classList.contains('add_to_cart_button') ||
           (button.getAttribute('name') === 'add' && !button.closest('form'));
  }
  
  // Set up observer to watch for new buttons
  function setupButtonObserver() {
    const observer = new MutationObserver(mutations => {
      let newButtonsAdded = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) { // Element node
              // Check if this node is a button or contains buttons
              if (isQuickAddButton(node) || node.querySelector('.quick-add-btn, .add-to-cart, .add_to_cart, button[name="add"]:not([form])')) {
                newButtonsAdded = true;
              }
            }
          });
        }
      });
      
      if (newButtonsAdded) {
        log('New buttons detected, fixing states');
        setTimeout(fixButtonStates, 100);
      }
    });
    
    // Start observing
    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
  }
  
  // Monitor cart drawer open/close events
  function monitorCartDrawer() {
    // Look for cart drawer open/close events
    const drawerSelectors = [
      '#CartDrawer', 
      '.drawer--right', 
      '.ajaxcart__drawer',
      '.cart-drawer',
      '[data-drawer-id="cart"]'
    ];
    
    // Function to reset all buttons when cart drawer closes
    const resetAllButtons = () => {
      log('Cart drawer closed, resetting all buttons');
      document.querySelectorAll('[data-button-state-fixed="true"]').forEach(button => {
        resetButtonState(button);
      });
    };
    
    // Set up observers for each potential drawer
    drawerSelectors.forEach(selector => {
      const drawer = document.querySelector(selector);
      if (drawer) {
        log('Found cart drawer, setting up observer', drawer);
        
        // Set up mutation observer for this drawer
        const observer = new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            if (mutation.type === 'attributes' && 
                (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
              
              // Check if drawer was closed
              if (drawer.style.display === 'none' || 
                  !drawer.classList.contains('is-open') || 
                  drawer.classList.contains('drawer--is-closed')) {
                resetAllButtons();
              }
            }
          });
        });
        
        // Observe the drawer for style and class changes
        observer.observe(drawer, {
          attributes: true,
          attributeFilter: ['style', 'class']
        });
      }
    });
    
    // Also listen for ESC key (common way to close drawers)
    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        setTimeout(resetAllButtons, 500);
      }
    });
    
    // Listen for clicks on close buttons
    document.addEventListener('click', function(event) {
      const target = event.target;
      
      // Check if this is a drawer close button
      if (target.classList.contains('drawer__close-button') || 
          target.classList.contains('close-drawer') ||
          target.closest('.drawer__close-button, .close-drawer, .cart-close')) {
        setTimeout(resetAllButtons, 500);
      }
    });
  }
})();